import { createRef, useState } from 'react';
import tw from 'twin.macro';

import { SolutionsIcon } from '@icons/SolutionsIcon';
import useOnClickOutside from '@app/hooks/useOnClickOutside';
import { UserApplication } from '@auth_module/services';
import { useSwitchUserApplication } from '@app/hooks/useSwitchUserApplication';
import { PromoHubApplicationIcon } from '@icons/PromoHubApplicationIcon';
import { ApiApplicationIcon } from '@icons/ApiApplicationIcon';
import { InsightsApplicationIcon } from '@icons/InsightsApplicationIcon';
import { useUser } from '@auth_module/hooks/useUser';
import { NoInsightsAppModal } from '@account_module/components/NoInsightsAppModal';

const Container = tw.div`relative`;
const TogglerContainer = tw.div`flex items-center justify-start gap-2`;
const Toggler = tw.button`p-[4px] rounded-[4px] bg-gray-2 hover:bg-gray-3 transition-all duration-100`;
const Menu = tw.ul`absolute left-0 bg-neutral-background z-50 w-auto min-w-[280px] border border-solid border-gray-1 p-[4px] rounded-[4px] shadow-dropdown mt-[8px]`;
const MenuItem = tw.li`px-[8px] py-[6px] bg-transparent hover:bg-gray-2 rounded-[2px] flex items-center justify-between transition-all duration-100`;
const MenuTitle = tw.p`text-left text-text-and-icon text-[12px] font-semibold px-[8px] py-[4px] mb-[6px]`;
const SolutionButton = tw.button`w-full flex items-center justify-start gap-[8px] [&>span]:whitespace-nowrap [&>span]:text-text-and-icon-muted [&>span]:text-[14px] [&>span]:tracking-[0.14px] [&>span]:font-medium`;
const Label = tw.div`px-[5px] text-[10px] text-dark-accent-invert bg-sec-3 rounded-[2px]`;
const Separator = tw.div`w-[1px] h-[17px] bg-gray-1`;

export const SolutionsMenu = () => {
  const { userApplications } = useUser();
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const [isNoInsightsAppModalOpen, setIsNoInsightsAppModalOpen] = useState(false);
  const containerRef = createRef<HTMLDivElement>();

  useOnClickOutside(containerRef, () => setIsVisible(false));

  const { switchTo } = useSwitchUserApplication();

  const handleNavigateToApp = async (app: UserApplication) => {
    setIsVisible(false);
    if (userApplications?.includes(app)) {
      switchTo(app);
      return;
    }

    if (app === 'audio-insights') {
      setIsNoInsightsAppModalOpen(true);
    }
  };

  if (!userApplications || !userApplications.includes('promohub')) return null;

  return (
    <Container ref={containerRef}>
      <TogglerContainer>
        <Toggler onClick={() => setIsVisible(!isVisible)} css={[isVisible && tw`bg-gray-3`]}>
          <SolutionsIcon tw='stroke-dark-accent-invert' />
        </Toggler>
        <Separator />
      </TogglerContainer>

      {isVisible && (
        <Menu>
          <MenuTitle>Switch Sounder Solutions:</MenuTitle>
          <MenuItem>
            <SolutionButton onClick={() => handleNavigateToApp('audio-insights')}>
              <InsightsApplicationIcon tw='stroke-dark-accent-invert' />
              <span>Audio Insights</span>
              <Label>Beta</Label>
            </SolutionButton>
          </MenuItem>

          <MenuItem>
            <SolutionButton onClick={() => handleNavigateToApp('promohub')}>
              <PromoHubApplicationIcon tw='stroke-dark-accent-invert' />
              <span>Sounder AI-Promotion Hub</span>
              <Label>Beta</Label>
            </SolutionButton>
          </MenuItem>

          <MenuItem>
            <SolutionButton onClick={() => handleNavigateToApp('api')}>
              <ApiApplicationIcon tw='stroke-dark-accent-invert' />
              <span>Sounder API</span>
            </SolutionButton>
          </MenuItem>
        </Menu>
      )}

      <NoInsightsAppModal
        isOpen={isNoInsightsAppModalOpen}
        onClose={() => setIsNoInsightsAppModalOpen(false)}
      />
    </Container>
  );
};
